import { useEffect, useMemo, useRef } from "react";
import { FireIcon } from "@heroicons/react/24/outline";
import { ArrowsRightLeftIcon, SparklesIcon } from "@heroicons/react/24/solid";
import { Sprout } from "lucide-react";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { BannerActionButton } from "./banner-action-button";
import { CollapsibleBanner } from "./collapsible-banner";
import { MagicMomentDrawer } from "./magic-moment-drawer";
import { useMagicMomentStore } from "./use-magic-moment-store";

interface MomentumAlertBannerProps {
  teenName?: string | null;
  teenGender?: "male" | "female" | null;
  teenId?: string | null;
  conversationId?: string | null;
  onStartHotTakes: () => void;
  onStartMagicMoment?: (text: string) => void;
  onStartSwap: () => void;
}

export function MomentumAlertBanner({
  teenName,
  teenGender,
  teenId,
  conversationId,
  onStartHotTakes,
  onStartMagicMoment,
  onStartSwap,
}: MomentumAlertBannerProps) {
  const { track } = useTrackEvent();
  const viewTracked = useRef(false);
  const bannerExpanded = useMagicMomentStore((s) => s.bannerExpanded);
  const currentView = useMagicMomentStore((s) => s.currentView);
  const toggleBanner = useMagicMomentStore((s) => s.toggleBanner);
  const navigateToView = useMagicMomentStore((s) => s.navigateToView);
  const collapse = useMagicMomentStore((s) => s.collapse);

  useEffect(() => {
    if (viewTracked.current) return;
    viewTracked.current = true;
    track(PostHogEvents.MOMENTUM_ALERT_BANNER_VIEW, {
      teen_id: teenId,
      conversation_id: conversationId,
    });
  }, [conversationId, teenId, track]);

  const actions = useMemo(
    () => [
      {
        id: "hot_takes",
        label: "Hot takes",
        icon: <FireIcon className="h-3 w-3" />,
        onClick: onStartHotTakes,
      },
      {
        id: "magic_moment",
        label: "Magic moment",
        icon: <SparklesIcon className="h-3 w-3" />,
        onClick: () => navigateToView("magic-moment"),
      },
      {
        id: "start_swap",
        label: "Start a Swap",
        icon: <ArrowsRightLeftIcon className="h-3 w-3" />,
        onClick: () => {
          collapse();
          onStartSwap();
        },
      },
    ],
    [collapse, navigateToView, onStartHotTakes, onStartSwap],
  );

  const handleToggle = () => {
    const newState = bannerExpanded ? "collapsed" : "expanded";
    track(PostHogEvents.MOMENTUM_ALERT_BANNER_TOGGLE, {
      teen_id: teenId,
      state: newState,
    });
    toggleBanner();
  };

  const handleAction = (action: string, callback: () => void) => {
    track(PostHogEvents.MOMENTUM_ALERT_ACTION_CLICK, {
      teen_id: teenId,
      action,
    });
    callback();
  };

  if (currentView === "magic-moment") {
    return (
      <MagicMomentDrawer
        teenGender={teenGender ?? null}
        teenId={teenId}
        onInsertToChat={onStartMagicMoment}
      />
    );
  }

  return (
    <CollapsibleBanner
      title="Momentum alert."
      icon={<Sprout className="h-6 w-6 text-text-primary" />}
      expanded={bannerExpanded}
      onToggle={handleToggle}
    >
      <div className="flex flex-col gap-2">
        <p className="text-sm leading-5 text-text-secondary">
          Based on their first few days, {teenName || "your teen"} is at risk of
          disengaging. Reach out today with something personal to give your
          relationship some momentum!
        </p>
        <div className="flex flex-wrap gap-x-1">
          {actions.map((action) => (
            <BannerActionButton
              key={action.id}
              icon={action.icon}
              label={action.label}
              onClick={() => handleAction(action.id, action.onClick)}
            />
          ))}
        </div>
      </div>
    </CollapsibleBanner>
  );
}
